<template>
    <AcademyLayout>
        <main class="container-fluid">
            <div class="groups-management-container">
                <div class="header-section">
                    <h2>{{ $t('navbar.group-management') }}</h2>
                    <b-button variant="primary" @click="showCreateModal = true">
                        <b-icon-plus-circle class="mr-2"></b-icon-plus-circle>
                        {{ $t('academy.new-group') }}
                    </b-button>
                </div>

                <!-- Filtros -->
                <div class="filters-section mb-4">
                    <b-row>
                        <b-col md="4">
                            <b-form-group :label="$t('academy.search-by-name')">
                                <b-form-input
                                    v-model="searchQuery"
                                    :placeholder="$t('academy.search-group')+'...'"
                                    @input="filterGroups"
                                ></b-form-input>
                            </b-form-group>
                        </b-col>
                        <b-col md="4">
                            <b-form-group :label="$t('academy.filter-by-mentor')">
                                <b-form-select
                                    v-model="selectedMentor"
                                    :options="mentorOptions"
                                    @change="filterGroups"
                                ></b-form-select>
                            </b-form-group>
                        </b-col>
                        <b-col md="4">
                            <b-form-group :label="$t('academy.filter-by-course')">
                                <b-form-select
                                    v-model="selectedCourse"
                                    :options="courseOptions"
                                    @change="filterGroups"
                                ></b-form-select>
                            </b-form-group>
                        </b-col>
                    </b-row>
                    <b-row class="mt-2">
                        <b-col>
                            <b-button 
                                variant="outline-secondary" 
                                @click="clearFilters"
                                :disabled="!hasActiveFilters"
                            >
                                <b-icon-x class="mr-1"></b-icon-x>
                                {{ $t('academy.clear-filters') }}
                            </b-button>
                        </b-col>
                    </b-row>
                </div>

                <!-- Tabla de Grupos -->
                <div class="table-responsive">
                    <b-table
                        :items="filteredGroups"
                        :fields="fields"
                        striped
                        hover
                        responsive
                        class="groups-table"
                    >
                        <template #cell(name)="data">
                            <div class="d-flex align-items-center">
                                <span>{{ data.item.name }}</span>
                            </div>
                        </template>

                        <template #cell(mentor)="data">
                            <span>{{ data.item.mentor?.name || $t('academy.no_mentor') }}</span>
                        </template>

                        <template #cell(course)="data">
                            <span>{{ data.item.course?.name || $t('academy.no_course') }}</span>
                        </template>

                        <template #cell(students)="data">
                            <b-badge variant="info">{{ $tc('academy.number_students', data.item.students?.length || 0) }}</b-badge>
                        </template>

                        <template #cell(actions)="data">
                            <div class="action-buttons">
                                <b-button
                                    variant="outline-primary"
                                    size="sm"
                                    @click="editGroup(data.item)"
                                    class="mr-2"
                                >
                                    <b-icon-pencil></b-icon-pencil>
                                </b-button>
                                <b-button
                                    variant="outline-success"
                                    size="sm"
                                    @click="manageStudents(data.item)"
                                    class="mr-2"
                                >
                                    <b-icon-people></b-icon-people>
                                </b-button>
                                <b-button
                                    variant="outline-danger"
                                    size="sm"
                                    @click="confirmDelete(data.item)"
                                >
                                    <b-icon-trash></b-icon-trash>
                                </b-button>
                            </div>
                        </template>
                    </b-table>
                </div>

                <!-- Modal para Crear/Editar Grupo -->
                <b-modal
                    v-model="showCreateModal"
                    :title="isEditing ? $t('academy.edit-group') : $t('academy.new-group')"
                    @ok="handleSaveGroup"
                    @hidden="resetForm"
                    size="lg"
                >
                    <b-form @submit.stop.prevent="handleSaveGroup">
                        <b-form-group :label="$t('academy.group-name')">
                            <b-form-input
                                v-model="groupForm.name"
                                required
                                :placeholder="$t('academy.enter-group-name')"
                            ></b-form-input>
                        </b-form-group>

                        <b-form-group :label="$t('academy.schedule')">
                            <b-form-input
                                v-model="groupForm.schedule"
                                required
                                :placeholder="$t('academy.schedule-example')"
                            ></b-form-input>
                        </b-form-group>

                        <b-form-group :label="$t('academy.course')">
                            <b-form-select
                                v-model="groupForm.course"
                                :options="courseOptions"
                                required
                            ></b-form-select>
                        </b-form-group>

                        <b-form-group :label="$t('academy.mentor')">
                            <b-form-select
                                v-model="groupForm.mentor"
                                :options="mentorOptions"
                                required
                            ></b-form-select>
                        </b-form-group>

                        <b-form-group :label="$t('academy.remote_mode')">
                            <div class="d-flex align-items-center">
                                <div class="custom-switch-remote" @click="groupForm.isRemote = !groupForm.isRemote">
                                    <div :class="['switch-track', groupForm.isRemote ? 'active' : 'inactive']">
                                        <span class="switch-knob">
                                            <span class="switch-icon">
                                                <template v-if="groupForm.isRemote">
                                                    <svg width="20" height="20" viewBox="0 0 20 20"><polyline points="4,11 9,16 16,5" style="fill:none;stroke:#22c55e;stroke-width:2"/></svg>
                                                </template>
                                                <template v-else>
                                                    <svg width="20" height="20" viewBox="0 0 20 20"><line x1="5" y1="5" x2="15" y2="15" style="stroke:#888;stroke-width:2"/><line x1="15" y1="5" x2="5" y2="15" style="stroke:#888;stroke-width:2"/></svg>
                                                </template>
                                            </span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </b-form-group>

                        <b-form-group :label="$t('academy.students')">
                            <b-form-group :label="$t('academy.filter_by_email_or_name')">
                                <b-form-input v-model="studentFilter" :placeholder="$t('academy.paste_or_type_email')+'...'"></b-form-input>
                            </b-form-group>
                            <b-form-select
                                v-model="selectedStudent"
                                :options="filteredAvailableStudents"
                                @change="addStudentToForm"
                            >
                                <template #first>
                                    <b-form-select-option :value="null" disabled>
                                       {{ $t('academy.select-student')}}
                                    </b-form-select-option>
                                </template>
                            </b-form-select>

                            <div class="selected-students mt-3">
                                <b-list-group>
                                    <b-list-group-item v-for="student in groupForm.students" :key="student.uid" class="d-flex justify-content-between align-items-center">
                                        <div class="student-info">
                                            <div class="student-name">{{ student.name }}</div>
                                            <div class="student-email">{{ student.email }}</div>
                                        </div>
                                        <b-button
                                            variant="outline-danger"
                                            size="sm"
                                            @click="removeStudentFromForm(student)"
                                        >
                                            <b-icon-trash></b-icon-trash>
                                        </b-button>
                                    </b-list-group-item>
                                </b-list-group>
                            </div>
                        </b-form-group>
                    </b-form>
                </b-modal>

                <!-- Modal para Gestionar Estudiantes -->
                <b-modal
                    v-model="showStudentsModal"
                    :title="$t('academy.manage-students')"
                    size="lg"
                >
                    <div class="students-management">
                        <div class="add-student-section mb-4">
                            <b-form-group :label="$t('academy.filter_by_email_or_name')">
                                <b-form-input v-model="studentFilterModal" :placeholder="$t('academy.paste_or_type_email')+'...'"></b-form-input>
                            </b-form-group>
                            <b-form-group :label="$t('academy.add-student')">
                                <b-form-select
                                    v-model="selectedStudent"
                                    :options="filteredAvailableStudentsModal"
                                    @change="addStudentToGroup"
                                ></b-form-select>
                            </b-form-group>
                        </div>

                        <div class="students-list">
                            <b-table
                                :items="currentGroupStudents"
                                :fields="studentFields"
                                striped
                                hover
                            >
                                <template #cell(actions)="data">
                                    <b-button
                                        variant="outline-danger"
                                        size="sm"
                                        @click="removeStudent(data.item)"
                                    >
                                        <b-icon-trash></b-icon-trash>
                                    </b-button>
                                </template>
                            </b-table>
                        </div>
                    </div>
                </b-modal>
            </div>
        </main>
    </AcademyLayout>
</template>

<script>
import AcademyLayout from '@/layouts/AcademyLayout.vue';
import groupsData from '@/data/groupsData';
import educationalInstitutionsData from '@/data/educationalInstitutionsData';
import { BIconPlusCircle, BIconPencil, BIconTrash, BIconPeople, BIconX } from 'bootstrap-vue';
import Swal from 'sweetalert2';
import { auth } from '@/data/firebaseConfig';

export default {
    name: 'GroupsManagementView',
    components: {
        AcademyLayout,
        BIconPlusCircle,
        BIconPencil,
        BIconTrash,
        BIconPeople,
        BIconX
    },
    data() {
        return {
            groups: [],
            filteredGroups: [],
            searchQuery: '',
            selectedMentor: null,
            selectedCourse: null,
            showCreateModal: false,
            showStudentsModal: false,
            isEditing: false,
            currentGroup: null,
            selectedStudent: null,
            groupForm: {
                name: '',
                schedule: '',
                course: null,
                mentor: null,
                students: [],
                isRemote: true
            },
            mentorOptions: [],
            courseOptions: [],
            availableStudents: [],
            studentFilter: '',
            studentFilterModal: ''
        };
    },
    computed: {
        fields() {
            return [
                { key: 'name', label: this.$t('academy.name') },
                { key: 'mentor', label: this.$t('academy.mentor') },
                { key: 'course', label: this.$t('academy.course') },
                { key: 'schedule', label: this.$t('academy.schedule') },
                { key: 'students', label: this.$t('academy.students') },
                { key: 'actions', label: this.$t('academy.actions') }
            ];
        },
        studentFields() {
            return [
                { key: 'name', label: this.$t('academy.name') },
                { key: 'email', label: 'Email' },
                { key: 'actions', label: this.$t('academy.actions') }
            ];
        },
        currentGroupStudents() {
            return this.currentGroup?.students || [];
        },
        hasActiveFilters() {
            return this.searchQuery || this.selectedMentor || this.selectedCourse;
        },
        filteredAvailableStudents() {
            if (!this.studentFilter) return this.availableStudents;
            return this.availableStudents.filter(s =>
                s.text.toLowerCase().includes(this.studentFilter.toLowerCase()) ||
                s.email.toLowerCase().includes(this.studentFilter.toLowerCase())
            );
        },
        filteredAvailableStudentsModal() {
            if (!this.studentFilterModal) return this.availableStudents;
            return this.availableStudents.filter(s =>
                s.text.toLowerCase().includes(this.studentFilterModal.toLowerCase()) ||
                s.email.toLowerCase().includes(this.studentFilterModal.toLowerCase())
            );
        }
    },
    async mounted() {
        await this.waitForAuth();
        await this.loadData();
    },
    methods: {
        waitForAuth() {
            return new Promise((resolve) => {
                const unsubscribe = auth.onAuthStateChanged((user) => {
                    unsubscribe();
                    resolve(user);
                });
            });
        },
        async loadData() {
            try {
                // Obtener usuario actual
                const user = auth.currentUser;
                if (!user) {
                    throw new Error('Usuario no autenticado');
                }
                // Obtener la institución del usuario (admin)
                const institution = await educationalInstitutionsData.getInstitutionByAdminUid(user.uid);
                if (!institution) {
                    throw new Error('No se encontró la institución del usuario');
                }
                // Obtener los ids de cursos de la sub-colección academyCourses
                const institutionCourseIds = await educationalInstitutionsData.getAcademyCourses(institution.id);
                // Cargar todos los grupos
                const allGroups = await groupsData.getAllGroups();
                // Filtrar los grupos cuyos cursos pertenezcan a la institución
                this.groups = allGroups.filter(group =>
                    group.course && institutionCourseIds.includes(group.course.uid)
                );
                this.filteredGroups = [...this.groups];

                // Cargar mentores de la institución
                const mentors = await educationalInstitutionsData.getMentors(institution.id);
                this.mentorOptions = mentors.map(mentor => ({
                    value: mentor.uid,
                    text: mentor.name
                }));

                // Cargar cursos (solo los de la institución)
                const allCourses = await groupsData.getAllCourses();
                this.courseOptions = allCourses.filter(course => institutionCourseIds.includes(course.value));

                // Cargar estudiantes disponibles
                const students = await groupsData.getAllStudents();
                this.availableStudents = students.map(student => ({
                    value: student.value,
                    text: `${student.text} - (${student.email})`,
                    email: student.email
                }));
            } catch (error) {
                console.error('Error al cargar datos:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'No se pudieron cargar los datos'
                });
            }
        },
        filterGroups() {
            this.filteredGroups = this.groups.filter(group => {
                const matchesSearch = group.name.toLowerCase().includes(this.searchQuery.toLowerCase());
                const matchesMentor = !this.selectedMentor || group.mentor?.uid === this.selectedMentor;
                const matchesCourse = !this.selectedCourse || group.course?.uid === this.selectedCourse;
                return matchesSearch && matchesMentor && matchesCourse;
            });
        },
        editGroup(group) {
            this.isEditing = true;
            this.currentGroup = group;
            this.groupForm = {
                name: group.name,
                schedule: group.schedule,
                course: group.course?.uid,
                mentor: group.mentor?.uid,
                students: group.students || [],
                isRemote: group.isRemote
            };
            this.showCreateModal = true;
        },
        async handleSaveGroup() {
            try {
                const groupData = {
                    name: this.groupForm.name,
                    schedule: this.groupForm.schedule,
                    course: {
                        uid: this.groupForm.course,
                        name: this.courseOptions.find(c => c.value === this.groupForm.course)?.text
                    },
                    mentor: {
                        uid: this.groupForm.mentor,
                        name: this.mentorOptions.find(m => m.value === this.groupForm.mentor)?.text
                    },
                    students: this.groupForm.students,
                    isRemote: this.groupForm.isRemote
                };

                if (this.isEditing) {
                    await groupsData.updateGroup(this.currentGroup.id, groupData);
                } else {
                    await groupsData.createGroup(groupData);
                }

                await this.loadData();
                this.showCreateModal = false;
                Swal.fire({
                    icon: 'success',
                    title: this.$t('academy.success'),
                    text: this.isEditing ? this.$t('academy.group-updated') : this.$t('academy.group-created')
                });
            } catch (error) {
                console.error('Error al guardar grupo:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'No se pudo guardar el grupo'
                });
            }
        },
        async confirmDelete(group) {
            const result = await Swal.fire({
                title: this.$t('academy.are-you-sure'),
                text: this.$t('academy.action-cannot-be-undone'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: this.$t('academy.confirm_delete'),
                cancelButtonText: this.$t('academy.cancel')
            });

            if (result.isConfirmed) {
                try {
                    await groupsData.deleteGroup(group.id);
                    await this.loadData();
                    Swal.fire({
                        icon: 'success',
                        title: this.$t('academy.deleted'),
                        text: this.$t('academy.group-deleted')
                    });
                } catch (error) {
                    console.error('Error al eliminar grupo:', error);
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: this.$t('academy.group-delete-failed')
                    });
                }
            }
        },
        manageStudents(group) {
            this.currentGroup = group;
            this.showStudentsModal = true;
        },
        async addStudentToGroup() {
            if (!this.selectedStudent) return;

            try {
                const studentData = {
                    uid: this.selectedStudent,
                    name: this.availableStudents.find(s => s.value === this.selectedStudent)?.text
                };

                await groupsData.assignStudentToGroup(this.currentGroup.id, studentData);
                await this.loadData();
                this.selectedStudent = null;
                Swal.fire({
                    icon: 'success',
                    title: this.$t('academy.success'),
                    text: this.$t('academy.student-added')
                });
            } catch (error) {
                console.error('Error al agregar estudiante:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: this.$t('academy.student-add-failed')
                });
            }
        },
        async removeStudent(student) {
            try {
                await groupsData.removeStudentFromGroup(this.currentGroup.id, student.uid);
                await this.loadData();
                Swal.fire({
                    icon: 'success',
                    title: this.$t('academy.success'),
                    text: this.$t('academy.student-removed')
                });
            } catch (error) {
                console.error('Error al remover estudiante:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: this.$t('academy.student-remove-failed')
                });
            }
        },
        addStudentToForm() {
            if (!this.selectedStudent) return;

            const student = this.availableStudents.find(s => s.value === this.selectedStudent);
            if (student && !this.groupForm.students.some(s => s.uid === student.value)) {
                this.groupForm.students.push({
                    uid: student.value,
                    name: student.text.split(' (')[0],
                    email: student.email
                });
            }
            this.selectedStudent = null;
        },
        removeStudentFromForm(student) {
            this.groupForm.students = this.groupForm.students.filter(s => s.uid !== student.uid);
        },
        resetForm() {
            this.isEditing = false;
            this.currentGroup = null;
            this.groupForm = {
                name: '',
                schedule: '',
                course: null,
                mentor: null,
                students: [],
                isRemote: true
            };
            this.selectedStudent = null;
        },
        clearFilters() {
            this.searchQuery = '';
            this.selectedMentor = null;
            this.selectedCourse = null;
            this.filterGroups();
        }
    }
};
</script>

<style lang="scss" scoped>
.groups-management-container {
    background: white;
    border-radius: 15px;
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
    padding: 20px;
    margin-top: 20px;
}

.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h2 {
        margin: 0;
        color: #333;
    }
}

.filters-section {
    background: #f8f9fa;
    padding: 15px;
    border-radius: 8px;
    margin-bottom: 20px;
}

.groups-table {
    margin-top: 20px;

    ::v-deep th {
        background-color: #f8f9fa;
        font-weight: 600;
    }
}

.action-buttons {
    display: flex;
    gap: 5px;
}

.students-management {
    .add-student-section {
        background: #f8f9fa;
        padding: 15px;
        border-radius: 8px;
    }

    .students-list {
        margin-top: 20px;
    }
}

::v-deep .modal-header {
    background-color: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
}

::v-deep .modal-footer {
    background-color: #f8f9fa;
    border-top: 1px solid #dee2e6;
}

.selected-students {
    max-height: 200px;
    overflow-y: auto;
}

.list-group-item {
    padding: 0.5rem 1rem;
}

.student-info {
    .student-name {
        font-weight: 500;
    }
    
    .student-email {
        font-size: 0.85rem;
        color: #6c757d;
    }
}

.input-group-append {
    .btn {
        padding: 0.375rem 0.75rem;
    }
}

.student-option {
    padding: 0.25rem 0;
    
    .student-name {
        font-weight: 500;
        margin-bottom: 0.125rem;
    }
    
    .student-email {
        font-size: 0.85rem;
        color: #6c757d;
    }
}

::v-deep .custom-select {
    option {
        padding: 8px;
    }
}

.custom-switch-remote {
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
    .switch-track {
        display: flex;
        align-items: center;
        width: 56px;
        height: 32px;
        border-radius: 20px;
        position: relative;
        transition: background 0.2s;
        background: #232232;
        &.active {
            background: #22c55e;
            .switch-knob {
                left: 30px;
                background: #eaeaea;
            }
        }
        &.inactive {
            background: #232232;
            .switch-knob {
                left: 4px;
                background: #eaeaea;
            }
        }
        .switch-knob {
            position: absolute;
            top: 4px;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            background: #eaeaea;
            transition: left 0.2s, background 0.2s;
            z-index: 3;
            box-shadow: 0 2px 6px rgba(0,0,0,0.15);
            display: flex;
            align-items: center;
            justify-content: center;
            .switch-icon {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 20px;
                height: 20px;
            }
        }
    }
}
</style> 